/**
 * React Query hooks for the menu screens. Each wraps an existing service
 * function so revisiting a screen serves cached data instantly and revalidates
 * in the background (see queryClient.ts for the cache policy).
 *
 * Query-key convention: [resource, ...scope]. After a mutation, invalidate the
 * matching key with useQueryClient().invalidateQueries({ queryKey: [...] }).
 */
import { useAuthedQuery } from './useAuthedQuery';
import { getAllClientsByBusinessId } from '../service/client/client-service';
import { getServicesByBusinessId } from '../service/service/service-service';
import { getCategoriesByBusinessId } from '../service/category/category-service';
import { getTeamMembersByBusinessId } from '../service/teammember/team-member-service';
import { findBusinessDetailsByBusinessId } from '../service/business/business-service';
import { getBusinessHealthPlans } from '../service/business/health-plan-service';
import { getTransactions } from '../service/payment/transaction-service';
import { getAppointmentsByBusinessAndRange } from '../service/appointment/appointment-service';
import { getNotifications } from '../service/notification/notification-service';
import { Notification } from '../model/notification/Notification';
import { getBusinessRoles } from '../service/role/business-role-service';
import { findBusinessHoursByBusinessId } from '../service/business/business-service';
import { RoleBusiness } from '../model/role/RoleBusiness';
import { BusinessHours } from '../model/business/BusinessHours';
import { Client } from '../model/client/Client';
import { Service } from '../model/service/Service';
import { Category } from '../model/category/Category';
import { TeamMember } from '../model/teammember/TeamMember';
import { BusinessDetails } from '../model/business/BusinessDetails';
import { HealthPlan } from '../model/business/HealthPlan';
import { TransactionPage, TransactionFilters } from '../model/payment/Transaction';
import { Appointment } from '../model/appointment/Appointment';

/** Clients for a business (Clients screen). */
export function useClients(businessId: string) {
    return useAuthedQuery<Client[]>(
        ['clients', businessId],
        (token) => getAllClientsByBusinessId(businessId, token),
        { enabled: !!businessId },
    );
}

/** Bookable/products services for a business (Services screen + others). */
export function useServices(businessId: number | string) {
    return useAuthedQuery<Service[]>(
        ['services', String(businessId)],
        (token) => getServicesByBusinessId(businessId, token),
        { enabled: !!businessId },
    );
}

/** Service categories. */
export function useCategories(businessId: number | string) {
    return useAuthedQuery<Category[]>(
        ['categories', String(businessId)],
        (token) => getCategoriesByBusinessId(businessId, token),
        { enabled: !!businessId },
    );
}

/** Team members. */
export function useTeamMembers(businessId: number | string) {
    return useAuthedQuery<TeamMember[]>(
        ['teamMembers', String(businessId)],
        (token) => getTeamMembersByBusinessId(businessId, token),
        { enabled: !!businessId },
    );
}

/** Business details (company tax rate, etc.). */
export function useBusinessDetails(businessId: number | undefined) {
    return useAuthedQuery<BusinessDetails | null>(
        ['businessDetails', String(businessId ?? '')],
        (token) => findBusinessDetailsByBusinessId(businessId, token).catch(() => null),
        { enabled: !!businessId },
    );
}

/** All notifications for the business (Notifications page). */
export function useNotifications(businessId: string, limit = 100) {
    return useAuthedQuery<Notification[]>(
        ['notifications', businessId, limit],
        (token) => getNotifications(businessId, token, limit),
        { enabled: !!businessId },
    );
}

/** Per-business roles (Team screen role badges). */
export function useBusinessRoles(businessId: string) {
    return useAuthedQuery<RoleBusiness[]>(
        ['businessRoles', businessId],
        (token) => getBusinessRoles(businessId, token),
        { enabled: !!businessId },
    );
}

/** Business working hours (default rows for team working-hours editor). */
export function useBusinessHours(businessId: number | undefined) {
    return useAuthedQuery<BusinessHours[]>(
        ['businessHours', String(businessId ?? '')],
        (token) => findBusinessHoursByBusinessId(businessId, token).catch(() => [] as BusinessHours[]),
        { enabled: !!businessId },
    );
}

/** Health plans (convênios) accepted by the business. */
export function useHealthPlans(businessId: number | string) {
    return useAuthedQuery<HealthPlan[]>(
        ['healthPlans', String(businessId)],
        (token) => getBusinessHealthPlans(Number(businessId), token).catch(() => [] as HealthPlan[]),
        { enabled: !!businessId },
    );
}

/** Paginated transactions for the Payments screen. The key carries the filters
 *  so each filter combination is cached separately. */
export function useTransactions(businessId: string, filters: TransactionFilters, enabled = true) {
    return useAuthedQuery<TransactionPage>(
        ['transactions', businessId, filters],
        (token) => getTransactions(businessId, filters, token),
        { enabled: enabled && !!businessId },
    );
}

/** Appointments in a date range (Appointments / Calendar screens). */
export function useAppointmentsByRange(businessId: string, startIso: string | null, endIso: string | null) {
    return useAuthedQuery<Appointment[]>(
        ['appointments', businessId, startIso, endIso],
        (token) => getAppointmentsByBusinessAndRange(businessId, startIso!, endIso!, token),
        { enabled: !!businessId && !!startIso && !!endIso },
    );
}
